import React, { ReactElement, useEffect } from 'react';

import PiralInstance from './PiralInstance';
import LoadingIndicator from './components/LoadingIndicator';
import ErrorMessage from './components/ErrorMessage';
import { useClientData } from './hooks/useClientData';
import { getUrlParams, formatConfigTitle } from './utils/urlParams';
import Environment, { ENV_VARS } from './Environment';
import { BackendData } from './utils/fetchData';

// Mock flag: Set to true to always show loading indicator
const MOCK_LOADING_INDICATOR = false;

const Client = (): ReactElement => {
  const { mode, configID, componentID } = getUrlParams();

  // Set environment variable
  if (configID) {
    Environment.set(ENV_VARS.CONFIG_ID, configID);
  }

  // Set the configID as the page title
  useEffect(() => {
    if (configID) {
      document.title = formatConfigTitle(configID);
    }
  }, [configID]);

  // Validate required parameters
  if (!configID && !componentID) {
    return <ErrorMessage message="Error: No configID provided" />;
  }

  console.info('configID: ' + configID);

  const { data, isReady, isFetchInProgress, error, layout, setLayout, triggerFetch, clearError } = useClientData(configID, componentID);

  // Handle preview mode
  if (layout === null && mode === 'preview') {
    setLayout('single');
    return <LoadingIndicator isFetchInProgress={isFetchInProgress} />;
  }

  // Show error if fetch failed
  if (error) {
    return (
      <ErrorMessage
        message={`Error loading application: ${error.message}`}
        onRetry={() => {
          clearError();
          triggerFetch();
        }}
      />
    );
  }

  // Fetch data if not already fetched
  if (!data) {
    if (isFetchInProgress) {
      return <LoadingIndicator isFetchInProgress={isFetchInProgress} />;
    }
    triggerFetch();
    return <LoadingIndicator isFetchInProgress={isFetchInProgress} />;
  }

  // Mock: Always show loading indicator when MOCK_LOADING_INDICATOR is true
  if (MOCK_LOADING_INDICATOR) {
    return <LoadingIndicator isFetchInProgress={isFetchInProgress} />;
  }

  // Show loading while not ready
  if (!isReady) {
    return <LoadingIndicator isFetchInProgress={isFetchInProgress} />;
  }

  return <PiralInstance data={data as BackendData & { pages: Record<string, unknown>; connectivity: unknown }} />;
};

export default Client;